import {Injectable, OnDestroy} from '@angular/core';
import {
  FormElement,
  FormElementPatch,
  FormPage,
  FormRadioOption, Validation,
  WebFormTemplate
} from '../models/WebFormTemplate';
import {AbstractControl, AsyncValidatorFn, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {regexValidator} from '../validators/RegexValidator';
import {requiredCheckboxValidator} from '../validators/RequiredTrueValidator';
import {CustomFormControl} from '../components/controls/CustomFormControl';
import {ainValidatorAndPatcher} from '../validators/AinPatcher';
import {ApiService} from '../../../_services/api.service';
import {noFutureDateValidator} from '../validators/NoFutureDateValidator';
import {ssnValidator} from '../validators/SsnValidator';
import {zipCodeValidator} from '../validators/ZipValidator';
import {uniqueArrayFieldValidator} from '../validators/UniqueArrayFieldValidator';
import {noCountyEmailValidator} from '../validators/NoCountyEmailValidator';
import {equalityValidator} from '../validators/EqualityValidator';
import {signixEmailLimitValidator} from '../validators/SignixEmailLimitValidator';
import {CustomFormGroup} from '../components/controls/CustomFormGroup';
import {CreatedFormInfo} from '../models/CreatedFormInfo';
import {FormFAQ} from '../models/api/FormFAQ';
import {Parcel} from '../../../_models/parcel';
import {Subject} from 'rxjs';
import {FileOnlineViewModel} from '../FileOnlineViewModel';
import {FileOnlineUtil} from '../FileOnlineUtil';
import {debounceTime, distinctUntilChanged, pairwise, startWith, takeUntil} from 'rxjs/operators';
import {minimumTemplateCountValidator} from '../validators/MinimumTemplateCountValidator';
import {atLeastOneCheckboxValidator} from '../validators/AtLeastOneCheckboxValidator';
import {getNestedValue} from './form-remapper';
import {ParcelInfoDataWarehouse} from '../../../_models/ParcelInfoDataWarehouse';
import {asyncPersonalPropertyValidator} from '../validators/AsyncPersonalPropertyValidator';
import {alphanumericValidator} from '../validators/AlphanumericValidator';

@Injectable({
  providedIn: 'root'
})
export class FormHelperService implements OnDestroy {

  private addressLookupSubject = new Subject<UntypedFormGroup>();
  addressLookupObservable = this.addressLookupSubject.asObservable();
  private destroySubj = new Subject<void>();

  constructor(private fb: UntypedFormBuilder,
              private apiService: ApiService) { }

  ngOnDestroy(): void {
    this.destroySubj.next();
    this.destroySubj.complete();
  }

  // --------- MISC HELPER ---------

  isMobileDevice(): boolean {
    const userAgent = window.navigator.userAgent;
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    return mobileRegex.test(userAgent);
  }

  isSafari(): boolean {
    const userAgent = navigator.userAgent;
    return /^((?!chrome|android).)*safari/i.test(userAgent);
  }

  openLinkInNewTab(url: string): void {
    window.open(url, '_blank');
  }

  openLinkInTab(url: string): void {
    window.location.href = url;
  }

  // --------- FORM GROUP HELPER ---------
  markFormAsTouched(formGroup: UntypedFormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof UntypedFormArray) {
        for (let i = 0; i < control.length; ++i) {
          const arrayControl = control.at(i);
          if (arrayControl instanceof UntypedFormGroup) {
            this.markFormAsTouched(arrayControl);
          } else {
            arrayControl.markAsTouched({ onlySelf: true });
          }
        }
      } else if (control instanceof UntypedFormGroup) {
        this.markFormAsTouched(control);
      } else {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  // --------- VISIBILITY HELPER ---------

  isFormPageVisible(formPage: FormPage, controls: Map<string, CustomFormControl>): boolean {
    if (!formPage.visibility) { return true; } // Not enforcing conditional rendering

    const customControl = controls.get(formPage.visibility.targetControl);
    if (!customControl) {
      console.error('Conditional rendering was enabled, but the control ID does not exist.');
      return true;
    }

    return customControl.element.option === formPage.visibility.targetValue;
  }

  // --------- CONTROL HELPER ---------

  getFormArrayName(id: string): string {
    return id + '_array';
  }

  getValidIndicator(control: AbstractControl | CustomFormControl): string {
    if (control instanceof CustomFormControl && (control.element.readOnly || control.disabled)) {
      return 'form-control';
    }

    if (control.touched && !control.valid) { return 'form-control is-invalid'; }
    if (control.touched && control.valid) { return 'form-control is-valid'; }
    return 'form-control';
  }

  // --------- FORM ELEMENT BUILDER ---------

  createForm(webFormTemplate: WebFormTemplate, faq: FormFAQ, viewModel: FileOnlineViewModel): CreatedFormInfo {
    const formPages: FormPage[] = [];
    const templates = new Map<string, FormElement[]>();
    const controls = new Map<string, CustomFormControl>();
    this.setupPrelude(faq, formPages);

    for (const [key, value] of Object.entries(webFormTemplate.templates)) {
      templates.set(key, value);
    }

    for (const formPage of webFormTemplate.pages) {
      const formGroup = this.fb.group({});

      const customFormControls: CustomFormControl[] = [];

      for (const formElement of formPage.elements) {
        const customFormControl = this.buildCustomFormControl(formElement, formGroup,
          templates, controls, viewModel, formGroup);
        formGroup.addControl(formElement.id, customFormControl);
        customFormControls.push(customFormControl);
        controls.set(formElement.id, customFormControl);

        this.setupInteractionTrigger(customFormControl, controls);

        if (formElement.type === 'file') {
          // Note: this may seem silly, but it's necessary. When the data is mapped
          // from the JSON to the TypeScript class, the list of strings is represented as
          // an Array, instead of a Set. The type safety does not catch this transformation
          // issue until functions are accessed at runtime.
          const allowedExtensions = new Set<string>();
          for (const extension of customFormControl.element.allowedExtensions) {
            allowedExtensions.add(extension);
          }

          customFormControl.element.allowedExtensions = allowedExtensions;
          customFormControl.element.uniqueFiles = new Set<string>();
          customFormControl.element.files = [];
          customFormControl.element.totalFileSize = 0;
          customFormControl.element.fileErrors = [];
        } else if (formElement.type === 'template') {
          const template = templates.get(formElement.id);
          if (!template) { continue; }

          const syncValidators = this.extractSyncValidators(formElement, controls);
          const formArray = this.fb.array([], syncValidators);
          for (let i = 0; i < formElement.initialElementCount; ++i) {
            formArray.push(this.buildFormGroupControlsForTemplate(template, templates, controls, viewModel, formGroup));
          }

          formGroup.addControl(this.getFormArrayName(formElement.id), formArray);
        } else if (formElement.type === 'existing_address') {
          // Manually set the default selection to the first radio option
          customFormControl.setValue('enter_manually', { emitEvent: false });
        } else if (formElement.type === 'grid') {
          // Create the NxM grid
          const gridControls: CustomFormControl[][] = [];

          // Loop the row structure
          for (const gridRowElements of formElement.grid) {
            // Create the row of CustomFormControl objects
            const gridRowControls: CustomFormControl[] = [];

            // Loop each gridRowElement
            for (const gridRowElement of gridRowElements) {
              // Create a custom form control
              const gridCustomFormControl = this.buildCustomFormControl(gridRowElement, formGroup,
                templates, controls, viewModel, formGroup);
              formGroup.addControl(gridRowElement.id, gridCustomFormControl);
              controls.set(gridRowElement.id, gridCustomFormControl);
              // Add it to the current row
              gridRowControls.push(gridCustomFormControl);
            }

            // Add this row to the parent grid
            gridControls.push(gridRowControls);
          }

          customFormControl.element.gridControls = gridControls;
        }
      }

      formPage.hasReturnBack = false;
      formPage.hasNavButtons = true;
      formPage.formGroup = formGroup;
      formPage.controls = customFormControls;
      formPages.push(formPage);
    }

    // Add dynamic pages
    this.addDynamicPage('Form Confirmation', 'form_summary', '_form_summary', true, formPages, templates, controls, viewModel);
    this.addDynamicPage('Form Preview', 'form_preview', '_form_preview', true, formPages, templates, controls, viewModel);
    this.addDynamicPage('Please wait', 'form_loading', '_form_loading', false, formPages, templates, controls, viewModel);
    this.addDynamicPage('Error During Submission', 'form_error', '_form_error', false, formPages, templates, controls, viewModel);
    this.addDynamicPage('Let\'s get your signature', 'signix_signature', '_signix_signature', false,
      formPages, templates, controls, viewModel);

    return new CreatedFormInfo(faq, formPages, templates, controls, webFormTemplate.specialMappings);
  }

  addDynamicPage(title: string,
                 type: string,
                 id: string,
                 hasNavButtons: boolean,
                 formPages: FormPage[],
                 templates: Map<string, FormElement[]>,
                 controls: Map<string, CustomFormControl>,
                 viewModel: FileOnlineViewModel): void {
    const formGroup = this.fb.group({});
    const formElement = new FormElement(type, title, id);
    const customFormControl = this.buildCustomFormControl(formElement, formGroup, templates, controls, viewModel, formGroup);
    formGroup.addControl(formElement.id, customFormControl);

    const formPage = new FormPage(title, [formElement]);
    formPage.hasNavButtons = hasNavButtons;
    formPage.formGroup = formGroup;
    formPage.controls = [customFormControl];
    formPage.type = type;
    formPages.push(formPage);
  }

  setupInteractionTrigger(control: CustomFormControl, controls: Map<string, CustomFormControl>): void {
    const interaction = control.element.interaction;
    if (!interaction) { return; }
    control.valueChanges.subscribe((value) => {
      for (const disableId of interaction.disableControls) {
        const isDisabled = (interaction.equalityType === 'equal' && interaction.disableValue === value) ||
          (interaction.equalityType === 'not_equal' && interaction.disableValue !== value);
        if (isDisabled) {
          controls.get(disableId)?.disable();
          if (interaction.resetTargetOnDisable === true) {
            controls.get(disableId)?.setValue('');
          }
        } else {
          controls.get(disableId)?.enable();
        }
      }
    });
  }

  setupPrelude(faq: FormFAQ, formPages: FormPage[]): void {
    const elementFormDesc = new FormElement('html', faq.faq, '_html');
    const elementPrivacyCheck = new FormElement('checkbox', '', '_privacy_acknowledgment');
    elementPrivacyCheck.option = 'By clicking here, you give your consent for the information to be collected. The data supplied will be handled and protected in accordance with the policies and practices of the County of Los Angeles and Office of the Assessor.';

    const ctrlHtml = new CustomFormControl('', elementFormDesc);
    const ctrlPrivacy = new CustomFormControl('',  elementPrivacyCheck, [requiredCheckboxValidator()], []);
    const formGroup = this.fb.group({});
    formGroup.addControl('_html', ctrlHtml);
    formGroup.addControl('_privacy_acknowledgment', ctrlPrivacy);

    const formPage = new FormPage(faq.formName, [elementFormDesc, elementPrivacyCheck]);
    formPage.formGroup = formGroup;
    formPage.controls = [ctrlHtml, ctrlPrivacy];
    formPages.push(formPage);
  }

  buildFormGroupControlsForTemplate(formElements: FormElement[],
                                    templates: Map<string, FormElement[]>,
                                    controlCache: Map<string, CustomFormControl>,
                                    viewModel: FileOnlineViewModel,
                                    pageFormGroup: UntypedFormGroup): UntypedFormGroup {
    const formGroup = new CustomFormGroup({}, []);
    for (const formElement of formElements) {
      const customFormControl = this.buildCustomFormControl(formElement, formGroup, templates,
        controlCache, viewModel, pageFormGroup);
      formGroup.addControl(formElement.id, customFormControl);
      formGroup.customFormControls.push(customFormControl);
      controlCache.set(formElement.id, customFormControl);
    }

    return formGroup;
  }

  buildCustomFormControl(formElement: FormElement,
                         formGroup: UntypedFormGroup,
                         templates: Map<string, FormElement[]>,
                         controls: Map<string, CustomFormControl>,
                         viewModel: FileOnlineViewModel,
                         pageFormGroup: UntypedFormGroup): CustomFormControl {
    const syncValidators = this.extractSyncValidators(formElement, controls);
    const specialValidators = this.extractSpecialValidators(formElement, controls);
    const asyncValidators = this.extractAsyncValidators(formElement.asyncValidators ?? '');
    this.appendPatcherValidators(formElement, formGroup, asyncValidators, viewModel); // Attach a patcher if necessary
    const finalControl = new CustomFormControl('', formElement, [...syncValidators, ...specialValidators], asyncValidators);
    this.appendPatcher(finalControl, formElement, viewModel, templates, controls, pageFormGroup);
    this.appendSpecialBehavior(finalControl, viewModel);
    this.appendControlVisibilityController(finalControl, controls);
    this.handleSpecialValidators(finalControl, formElement, controls);
    this.handleChangeActions(finalControl, formElement, controls);
    return finalControl;
  }

  handleChangeActions(finalControl: CustomFormControl,
                      formElement: FormElement,
                      controls: Map<string, CustomFormControl>): void {
    if (!formElement.onChange) {
      return;
    }

    for (const action of formElement.onChange.actions) {
      finalControl.valueChanges.subscribe(() => {
        const target = controls.get(action.targetControlId);
        if (!target) {
          return;
        }

        switch (action.action) {
          case 'enable_if_checked':
            target.element.readOnly = finalControl.value === false;
            break;
          case 'clear_if_unchecked':
            if (finalControl.value === false) {
              target.setValue('', { emitEvent: false });
            }
            break;
          default:
            break;
        }
      });
    }
  }

  extractSpecialValidators(formElement: FormElement, controls: Map<string, CustomFormControl>): ValidatorFn[] {
    if (!formElement.validations) {
      return [];
    }

    const validators: ValidatorFn[] = [];

    formElement.validations.forEach((validation: Validation) => {
      switch (validation.type) {
        case 'atLeastOneCheckbox':
          validators.push(atLeastOneCheckboxValidator(validation, controls));
          break;
      }
    });

    return validators;
  }

  handleSpecialValidators(control: CustomFormControl,
                          formElement: FormElement,
                          controls: Map<string, CustomFormControl>): void {
    if (!formElement.validations) {
      return;
    }

    formElement.validations.forEach((validation: Validation) => {
      switch (validation.type) {
        case 'atLeastOneCheckbox':
          // For the controls in this group
          validation.controls.forEach((targetControl) => {
            // Check if one changes
            controls.get(targetControl).valueChanges.subscribe(([previous, _]) => {
              // And check if ANY of the controls in the group are selected
              control.updateValueAndValidity();
            });
          });
          break;
      }
    });
  }

  appendControlVisibilityController(finalControl: CustomFormControl,
                                    controls: Map<string, CustomFormControl>): void {
    const visibility = finalControl.element.visibility;

    if (!visibility) {
      return;
    }

    const targetControl = controls.get(visibility.targetControl);

    if (!targetControl) {
      return;
    }

    targetControl.valueChanges
      .pipe(
        debounceTime(100),
        distinctUntilChanged(),
        takeUntil(this.destroySubj)
      )
      .subscribe((value: string) => {
        let currentValue = '';
        targetControl.element.options.forEach((option: FormRadioOption) => {
          if (option.label === value) {
            currentValue = option.id;
          }
        });

        if (visibility.comparison === 'equal') {
          finalControl.element.isVisible = currentValue === visibility.targetValue;
        } else {
          finalControl.element.isVisible = currentValue !== visibility.targetValue;
        }

        if (finalControl.element.isVisible) {
          finalControl.enable({ onlySelf: true });
        } else {
          finalControl.disable({ onlySelf: true });
        }

        finalControl.updateValueAndValidity();
      });
  }

  extractSyncValidators(field: FormElement, controls: Map<string, CustomFormControl>): ValidatorFn[] {
    const validatorList: ValidatorFn | ValidatorFn[] = [];

    if (field.regex) {
      validatorList.push(regexValidator(field.regex));
    }

    const syncValidators = field.syncValidators ?? '';
    syncValidators.split(',').forEach(validator => {
      const parts = validator.split(':');
      switch (parts[0]) {
        case 'required':
          validatorList.push(Validators.required);
          break;
        case 'requiredTrue':
          validatorList.push(requiredCheckboxValidator());
          break;
        case 'noFutureDate':
          validatorList.push(noFutureDateValidator());
          break;
        case 'ssn':
          validatorList.push(ssnValidator());
          break;
        case 'zip':
          validatorList.push(zipCodeValidator());
          break;
        case 'uniqueAin':
          validatorList.push(uniqueArrayFieldValidator('AIN #{i}'));
          break;
        case 'noCountyEmail':
          validatorList.push(noCountyEmailValidator());
          break;
        case 'signixEmailLimitValidator':
          validatorList.push(signixEmailLimitValidator());
          break;
        case 'email':
          validatorList.push(Validators.email);
          break;
        case 'maxLen':
        case 'minVal':
        case 'maxVal':
          if (parts.length === 2) {
            const parsedNumber = parseInt(parts[1], 10);
            if (!isNaN(parsedNumber)) {
              switch (parts[0]) {
                case 'maxLen':
                  validatorList.push(Validators.maxLength(parsedNumber));
                  break;
                case 'minVal':
                  validatorList.push(Validators.min(parsedNumber));
                  break;
                case 'maxVal':
                  validatorList.push(Validators.max(parsedNumber));
                  break;
              }
            }
          }

          break;
        case 'minimumTemplateCount':
          validatorList.push(minimumTemplateCountValidator(field.minimum));
          break;
        case 'alphanumeric':
          validatorList.push(alphanumericValidator());
          break;
        case '':
          break; // Ignore blank strings
        default:
          console.error(`Unknown validator provided ${validator}`);
          break;
      }
    });

    if (field.equalityValidator) {
      validatorList.push(equalityValidator(field.equalityValidator, controls));
    }

    return validatorList;
  }

  extractAsyncValidators(asyncValidators: string | null): AsyncValidatorFn[] {
    if (!asyncValidators) { return []; }
    const asyncValidatorsList: AsyncValidatorFn | AsyncValidatorFn[] = [];
    asyncValidators.split(',').forEach(validator => {
      switch (validator) {
        case 'personalProperty':
          asyncValidatorsList.push(asyncPersonalPropertyValidator(this.apiService));
          break;
      }
    });
    return asyncValidatorsList;
  }

  appendPatcherValidators(formElement: FormElement, formGroup: UntypedFormGroup,
                          asyncValidators: AsyncValidatorFn[], viewModel: FileOnlineViewModel): void {
    // If this field has a patcher, that means that, during the validation stage,
    // the input for another field will be updated with a transformation of this data.
    if (!formElement.patcher) { return; }

    switch (formElement.patcher.type) {
      case 'ain_to_address':
        asyncValidators.push(ainValidatorAndPatcher(formGroup, this.apiService, viewModel));
        break;
    }
  }

  appendPatcher(formControl: CustomFormControl,
                formElement: FormElement,
                viewModel: FileOnlineViewModel,
                templates: Map<string, FormElement[]>,
                controlCache: Map<string, CustomFormControl>,
                pageFormGroup: UntypedFormGroup): void {
    if (!formElement.patcher) { return; }

    switch (formElement.patcher.type) {
      case 'existing_address':
        this.setupExistingAddressPatcher(formControl, formElement, viewModel, controlCache);
        break;
      case 'address_search':
        this.setupAddressSearchPatcher(formControl, formElement, templates, controlCache, viewModel, pageFormGroup);
        break;
    }
  }

  setupAddressSearchPatcher(formControl: CustomFormControl,
                            formElement: FormElement,
                            templates: Map<string, FormElement[]>,
                            controlCache: Map<string, CustomFormControl>,
                            viewModel: FileOnlineViewModel,
                            pageFormGroup: UntypedFormGroup): void {
    formControl.valueChanges.subscribe((value: Parcel) => {
      if (formElement.patcher.targetTemplate !== undefined) {
        const formArrayName = this.getFormArrayName(formElement.patcher.targetTemplate);
        const formArray = pageFormGroup.get(formArrayName) as UntypedFormArray;
        if (!formArray) {
          console.error(`No template array ${formArrayName} found in page group`);
          return;
        }

        const template = templates.get(formElement.patcher.targetTemplate);
        let formGroupToUpdate: UntypedFormGroup = null;

        // Find the first invalid control
        for (const arrayControl of formArray.controls) {
          const formArrayFormGroupEntry = arrayControl as UntypedFormGroup;
          const controlAin = formArrayFormGroupEntry.controls[template[0].id];
          if (!controlAin.valid) {
            formGroupToUpdate = formArrayFormGroupEntry;
            break;
          }
        }

        // Ensure we haven't hit the size limit
        if (formArray.length + 1 > formControl.element.maximum) {
          return; // At maximum
        }

        // Ensure change detections are made
        if (formGroupToUpdate) {
          this.patchAddressToAinAddressControl(formGroupToUpdate, value, template);
          return;
        }

        const newFormGroup = this.buildFormGroupControlsForTemplate(template, templates,
          controlCache, viewModel, pageFormGroup);
        this.patchAddressToAinAddressControl(newFormGroup, value, template);
        // formArray.push(newFormGroup);
        this.addressLookupSubject.next(newFormGroup);
        return;
      }

      this.handleParcelPatching(value, formElement.patcher.targets, controlCache);

      if (formElement.patcher.subPatcher === undefined) {
        return;
      }

      this.apiService.getParcelInfo(value.AIN).subscribe((parcelInfo: ParcelInfoDataWarehouse) => {
        this.handleParcelPatching(parcelInfo, formElement.patcher.subPatcher.targets, controlCache);
      });
    });
  }

  patchAddressToAinAddressControl(formArrayFormGroupEntry: UntypedFormGroup, value: Parcel, template: FormElement[]): void {
    // The first control is always the AIN, the second is always the single line address
    const controlAin = formArrayFormGroupEntry.controls[template[0].id];
    const controlAddress = formArrayFormGroupEntry.controls[template[1].id];
    controlAin.setValue(FileOnlineUtil.insertDashesAin(value.AIN));
    controlAddress.setValue(value.SitusStreet + ', ' + value.SitusCity + ', ' + value.SitusZipCode);
  }

  setupExistingAddressPatcher(formControl: CustomFormControl,
                              formElement: FormElement,
                              viewModel: FileOnlineViewModel,
                              controlCache: Map<string, CustomFormControl>): void {
    formControl.valueChanges.subscribe((value: string) => {
      if (value === 'enter_manually') {
        formElement.patcher.targets.forEach(target => {
          controlCache.get(target.targetControlId)?.reset();
        });
        return;
      }

      const cachedParcel = viewModel.getCachedParcel(value);
      if (!cachedParcel) { return; }
      if (!FileOnlineUtil.areAinsEqual(cachedParcel.AIN, value)) { return; }

      this.handleParcelPatching(cachedParcel, formElement.patcher.targets, controlCache);
    });
  }

  handleParcelPatching(parcel: any,
                       patches: FormElementPatch[],
                       controlCache: Map<string, CustomFormControl>): void {
    for (const patch of patches) {
      const targetControl = controlCache.get(patch.targetControlId);

      if (targetControl === undefined) {
        continue;
      }

      if (patch.makeTargetReadOnly === true) {
        targetControl.element.readOnly = true;
      }

      if (!patch.sourceField) {
        targetControl.setValue(patch.targetControlValue);
        continue;
      }

      const value = getNestedValue(parcel, patch.sourceField);

      if (patch.sourceField === 'AIN') {
        targetControl.setValue(FileOnlineUtil.insertDashesAin(parcel.AIN));
        continue;
      }

      targetControl.setValue(value);
    }
  }

  private appendSpecialBehavior(finalControl: CustomFormControl, viewModel: FileOnlineViewModel): void {
    switch (finalControl.element.type) {
      case 'ain':
        finalControl.valueChanges.pipe(startWith(''), pairwise()).subscribe(([previous, _]) => {
          // console.log('Previous: ' + previous + ', Current: ' + current);
          if (previous && previous.length === 12) {
            viewModel.removeParcel(previous);
          }
        });
        break;
      default:
        break;
    }
  }

  handleArrayDelete(formGroup: CustomFormGroup, viewModel: FileOnlineViewModel): void {
    // Deleting an item from a template array might have a rippling effect
    Object.values(formGroup.controls).forEach(control => {
      if (!(control instanceof CustomFormControl)) { return; }
      switch (control.element.type) {
        case 'ain':
          viewModel.removeParcel(control.value);
          break;
        default:
          break;
      }
    });
  }
}
